import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { 
  ArrowLeft, MapPin, Calendar, Package, Truck, User, Clock, 
  CheckCircle, XCircle, AlertCircle, Play, Pause, Route, Weight,
  FileText, Edit, Trash2, Loader2, Eye, RefreshCw, FileCheck,
  Phone, Building, DollarSign, Upload, Image, FileImage, Camera,
  CreditCard, File, Copy, Search, X, Users, Plus, Minus, Hash, Scale
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import TransferDocumentsSection from '../../components/TransferDocumentsSection';
import LoadingNotesSection from '../../components/operations/LoadingNotesSection';
import LoadingNoteWizardModal from '../../components/operations/LoadingNoteWizardModal';
import LoadingNotesV3Panel from '../../components/operations/LoadingNotesV3Panel';
import OperationOCRDocuments from '../../components/OperationOCRDocuments';
import { useOperationDocuments } from '../../hooks/useOperationDocuments';
import usePermissions from '../../contexts/PermissionsContext';
import DocumentsComplianceCenter from '../../components/operations/documents/DocumentsComplianceCenter';
import DocumentsComplianceRail from '../../components/operations/documents/DocumentsComplianceRail';
import AddDocumentModal from '../../components/operations/documents/AddDocumentModal';
import RequestFieldModal from '../../components/operations/documents/RequestFieldModal';
import CreateTransferWizard from '../../components/CreateTransferWizard';
import TransportMap from '../../components/TransportMap';
import TripDetailsBlock from '../../components/transfers/TripDetailsBlock';
import TransferStopsTimeline from '../../components/transfers/TransferStopsTimeline';
import { cn } from '../../lib/utils';
import { formatVehicleType, formatOperationType, formatMerchandiseType } from '../../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration des statuts
const STATUS_CONFIG = {
  planifiee: { label: 'Planifié', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: Clock },
  planned: { label: 'Planifié', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: Clock },
  en_cours_de_chargement: { label: 'En chargement', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Package },
  en_transit: { label: 'En transit', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Route },
  en_cours_de_dechargement: { label: 'En déchargement', color: 'bg-purple-100 text-purple-700 border-purple-300', icon: MapPin },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Play },
  completed: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle }
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.planned;
  const Icon = config.icon;
  return (
    <Badge className={cn('flex items-center gap-1.5 px-3 py-1 text-sm border', config.color)}>
      <Icon className="w-4 h-4" />
      {config.label}
    </Badge>
  );
};

const TransferDetail = () => {
  const { id, runId } = useParams();
  const transferId = runId || id;  // Support both URL patterns
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showForceCancelModal, setShowForceCancelModal] = useState(false);
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [forceCancelReason, setForceCancelReason] = useState('');
  const [permanentDeleteConfirm, setPermanentDeleteConfirm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showWizard, setShowWizard] = useState(false);

  // Documents — centre de conformité (transfert)
  const docData = useOperationDocuments(transferId, 'transfer');
  const { hasPermission } = usePermissions();
  const [addDocState, setAddDocState] = useState({ open: false, type: 'autre', files: [] });
  const [reqFieldState, setReqFieldState] = useState({ open: false, labels: [] });
  const openAddDoc = (type = 'autre', files = []) => setAddDocState({ open: true, type, files: files ? Array.from(files) : [] });
  const openRequestField = (labels = []) => setReqFieldState({ open: true, labels: labels && labels.length ? labels : docData.stats.manquantsList });
  const [wizardMode, setWizardMode] = useState('edit'); // 'edit' or 'duplicate'
  const [editForm, setEditForm] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [uploading, setUploading] = useState(false);
  const [showAssignTruckModal, setShowAssignTruckModal] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [assignmentForm, setAssignmentForm] = useState({
    vehicleId: '',
    vehiclePlate: '',
    driverId: '',
    driverName: ''
  });
  // S1: Search in assign modal
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState('');
  // Bons de chargement/déchargement (PM Spec LOT 2 — 05/05/2026)
  const [loadingNoteModal, setLoadingNoteModal] = useState({ open: false, type: null });
  const [driverSearchTerm, setDriverSearchTerm] = useState('');
  // S2: Operator/FieldOps assignment
  const [showOperatorModal, setShowOperatorModal] = useState(false);
  const [showFieldOpsModal, setShowFieldOpsModal] = useState(false);
  const [operators, setOperators] = useState([]);
  const [fieldOpsUsers, setFieldOpsUsers] = useState([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState('');
  const [selectedFieldOpsId, setSelectedFieldOpsId] = useState('');
  const [savingAssignment, setSavingAssignment] = useState(false);
  // S3: Number of trips
  const [editingTrips, setEditingTrips] = useState(false);
  const [tripsValue, setTripsValue] = useState(1);
  // S3b: Net weight per trip
  const [editingNetWeight, setEditingNetWeight] = useState(false);
  const [tripsNetWeights, setTripsNetWeights] = useState([]);
  
  // Statuts qui permettent la modification
  const MODIFIABLE_STATUSES = ['planned', 'planifiee', 'en_cours_de_chargement'];
  // Statuts avec modifications très limitées (chauffeur/véhicule uniquement)
  const LIMITED_EDIT_STATUSES = ['en_transit', 'en_cours_de_dechargement'];
  
  // Vérifier si le transfert peut être modifié (mode standard)
  const canEdit = transfer && (
    MODIFIABLE_STATUSES.includes(transfer.status) || 
    LIMITED_EDIT_STATUSES.includes(transfer.status)
  );

  // Admin/opérateur peut toujours modifier les infos administratives, peu importe le statut
  const canAdminEdit = user?.role === 'admin' || user?.role === 'lead_ops' || user?.role === 'ops' || user?.role === 'operateur';
  
  // Vérifier les champs éditables selon le statut
  const getEditableFields = () => {
    if (!transfer) return { all: false };
    const status = transfer.status;
    
    if (MODIFIABLE_STATUSES.includes(status)) {
      return { all: true, trajet: true, resources: true, details: true };
    }
    if (LIMITED_EDIT_STATUSES.includes(status)) {
      return { all: false, trajet: false, resources: true, details: false };
    }
    return { all: false, trajet: false, resources: false, details: false };
  };
  
  // File input refs
  const loadingDocRef = useRef(null);
  const unloadingDocRef = useRef(null);
  const loadingPhotoRef = useRef(null);
  const unloadingPhotoRef = useRef(null);
  const permitRef = useRef(null);

  // Handle edit with wizard
  const handleEditWithWizard = () => {
    setWizardMode('edit');
    setShowWizard(true);
  };

  // Handle duplicate
  const handleDuplicate = () => {
    setWizardMode('duplicate');
    setShowWizard(true);
  };

  // Handle wizard success
  const handleWizardSuccess = () => {
    setShowWizard(false);
    fetchTransfer();
    if (wizardMode === 'duplicate') {
      toast.success('Transfert dupliqué avec succès !');
    }
  };
  
  // Fetch transfer data
  const fetchTransfer = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contract-runs/${transferId}`);
      setTransfer(response.data);
      // Initialiser les poids nets
      const nTrips = response.data.numberOfTrips || 1;
      const existingWeights = response.data.tripsNetWeights || [];
      setTripsNetWeights(Array.from({ length: nTrips }, (_, i) => existingWeights[i] ?? ''));
      setEditForm({
        departure_address: response.data.stops?.[0]?.address || '',
        arrival_address: response.data.stops?.[response.data.stops?.length - 1]?.address || '',
        vehicle_plate: response.data.plannedVehiclePlate || '',
        driver_name: response.data.plannedDriverName || '',
        weight: response.data.weight || '',
        merchandiseType: response.data.merchandiseType || '',
        notes: response.data.notes || ''
      });
    } catch (error) {
      toast.error('Erreur lors du chargement du transfert');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [transferId]);

  useEffect(() => {
    fetchTransfer();
  }, [fetchTransfer]);

  // Fetch available drivers and vehicles for assignment
  const fetchAvailableResources = useCallback(async () => {
    try {
      const [driversRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/drivers?status=active&limit=100`),
        axios.get(`${API}/vehicles?status=active&limit=100`)
      ]);
      setAvailableDrivers(driversRes.data.items || driversRes.data || []);
      setAvailableVehicles(vehiclesRes.data.items || vehiclesRes.data || []);
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  }, []);

  // Handle opening the assign truck modal
  const handleOpenAssignTruckModal = () => {
    fetchAvailableResources();
    setAssignmentForm({
      vehicleId: transfer.vehicleId || '',
      vehiclePlate: transfer.plannedVehiclePlate || '',
      driverId: transfer.driverId || '',
      driverName: transfer.plannedDriverName || ''
    });
    setShowAssignTruckModal(true);
  };

  // Handle assigning truck
  const handleAssignTruck = async () => {
    if (!assignmentForm.vehiclePlate && !assignmentForm.driverName) {
      toast.error('Veuillez sélectionner au moins un véhicule ou un chauffeur');
      return;
    }

    setSaving(true);
    try {
      await axios.patch(`${API}/contract-runs/${transferId}`, {
        plannedVehiclePlate: assignmentForm.vehiclePlate,
        plannedDriverName: assignmentForm.driverName,
        vehicleId: assignmentForm.vehicleId || null,
        driverId: assignmentForm.driverId || null
      });
      toast.success('Assignation mise à jour avec succès');
      setShowAssignTruckModal(false);
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'assignation');
    } finally {
      setSaving(false);
    }
  };

  // S2: Fetch operators and field ops users
  const fetchStaffUsers = useCallback(async (role) => {
    try {
      const res = await axios.get(`${API}/internal-users/operators/list?is_active=true`);
      return res.data || [];
    } catch { return []; }
  }, []);

  const handleOpenOperatorModal = async () => {
    const ops = await fetchStaffUsers('operateur');
    setOperators(ops);
    setSelectedOperatorId(transfer?.assignedOperatorId || '');
    setShowOperatorModal(true);
  };

  const handleOpenFieldOpsModal = async () => {
    try {
      const res = await axios.get(`${API}/internal-users/operators/list?is_active=true`);
      setFieldOpsUsers(res.data || []);
    } catch { setFieldOpsUsers([]); }
    setSelectedFieldOpsId(transfer?.assignedFieldOpsId || '');
    setShowFieldOpsModal(true);
  };

  const handleAssignOperator = async () => {
    if (!selectedOperatorId) return;
    setSavingAssignment(true);
    try {
      const op = operators.find(o => o.id === selectedOperatorId);
      await axios.patch(`${API}/contract-runs/${transferId}`, {
        assignedOperatorId: selectedOperatorId,
        assignedOperatorName: op ? `${op.firstName || ''} ${op.lastName || ''}`.trim() : ''
      });
      toast.success('Opérateur assigné');
      setShowOperatorModal(false);
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setSavingAssignment(false); }
  };

  const handleAssignFieldOps = async () => {
    if (!selectedFieldOpsId) return;
    setSavingAssignment(true);
    try {
      const fo = fieldOpsUsers.find(o => o.id === selectedFieldOpsId);
      await axios.patch(`${API}/contract-runs/${transferId}`, {
        assignedFieldOpsId: selectedFieldOpsId,
        assignedFieldOpsName: fo ? `${fo.firstName || ''} ${fo.lastName || ''}`.trim() : ''
      });
      toast.success('Field Ops assigné');
      setShowFieldOpsModal(false);
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setSavingAssignment(false); }
  };

  // S3: Save number of trips
  const handleSaveTrips = async () => {
    if (tripsValue < 1) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/contract-runs/${transferId}`, { numberOfTrips: tripsValue });
      // Réajuster le tableau des poids nets
      setTripsNetWeights(prev => Array.from({ length: tripsValue }, (_, i) => prev[i] ?? ''));
      toast.success(`Nombre de voyages mis à jour : ${tripsValue}`);
      setEditingTrips(false);
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  // S3b: Save net weights per trip
  const handleSaveNetWeights = async () => {
    setSaving(true);
    try {
      const weights = tripsNetWeights.map(w => w === '' || w === null ? null : parseFloat(w));
      const totalNet = weights.reduce((s, w) => s + (w || 0), 0);
      await axios.patch(`${API}/contract-runs/${transferId}/net-weight`, {
        tripsNetWeights: weights,
        netWeight: totalNet > 0 ? totalNet : null
      });
      toast.success(`Poids net enregistré : ${totalNet.toFixed(1)} T`);
      setEditingNetWeight(false);
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde du poids net');
    } finally { setSaving(false); }
  };

  // Mettre à jour le statut d'une étape (PM Spec 01/06/2026 — Multi-étapes)
  const handleStopStatusChange = async (stopId, newStatus) => {
    try {
      await axios.patch(
        `${API}/contract-runs/${transferId}/stops/${stopId}/status`,
        null,
        { params: { status: newStatus } }
      );
      toast.success(`Étape mise à jour : ${newStatus}`);
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour de l\'étape');
      throw error;
    }
  };

  // File upload handler
  const handleFileUpload = async (file, documentType) => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    
    try {
      await axios.post(`${API}/contract-runs/${transferId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploadé avec succès');
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  // Delete document handler
  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    
    try {
      await axios.delete(`${API}/contract-runs/${transferId}/documents/${documentId}`);
      toast.success('Document supprimé');
      fetchTransfer();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Workflow actions
  const handleStartLoading = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/contract-runs/${transferId}/start-loading`);
      toast.success('Chargement démarré');
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleStartTransit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/contract-runs/${transferId}/start-transit`);
      toast.success('Transit démarré');
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleStartUnloading = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/contract-runs/${transferId}/start-unloading`);
      toast.success('Déchargement démarré');
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleFinishUnloading = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/contract-runs/${transferId}/finish-unloading`);
      toast.success('Transfert terminé');
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    const reason = window.prompt("Raison de l'annulation :");
    if (!reason) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/contract-runs/${transferId}/cancel`, { reason });
      toast.success('Transfert annulé');
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  // Edit transfer
  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      // Update trajet
      if (editForm.departure_address || editForm.arrival_address) {
        await axios.patch(`${API}/contract-runs/${transferId}/trajet`, {
          departure_address: editForm.departure_address,
          arrival_address: editForm.arrival_address
        });
      }
      
      // Update resources
      if (editForm.vehicle_plate || editForm.driver_name) {
        await axios.patch(`${API}/contract-runs/${transferId}/resources`, {
          vehicle_plate: editForm.vehicle_plate,
          driver_name: editForm.driver_name
        });
      }
      
      // Update weight
      if (editForm.weight) {
        await axios.patch(`${API}/contract-runs/${transferId}/weight`, {
          weight: parseFloat(editForm.weight)
        });
      }
      
      // Update details
      if (editForm.merchandiseType || editForm.notes) {
        await axios.patch(`${API}/contract-runs/${transferId}/details`, {
          merchandise_type: editForm.merchandiseType,
          notes: editForm.notes
        });
      }
      
      toast.success('Transfert mis à jour');
      setShowEditModal(false);
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // Delete transfer
  const handleDelete = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/contract-runs/${transferId}`);
      toast.success('Transfert supprimé');
      navigate(-1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
      setShowDeleteModal(false);
    }
  };

  // Force cancel transfer (admin only — works even on completed)
  const handleForceCancel = async () => {
    if (!forceCancelReason.trim()) {
      toast.error('Le motif d\'annulation est obligatoire');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/contract-runs/${transferId}/force-cancel`, { reason: forceCancelReason.trim() });
      toast.success('Transfert annulé');
      setShowForceCancelModal(false);
      setForceCancelReason('');
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setSaving(false);
    }
  };

  // Permanent delete (admin only — irreversible)
  const handlePermanentDelete = async () => {
    if (permanentDeleteConfirm !== transfer?.runNumber) {
      toast.error('Veuillez saisir le numéro exact du transfert pour confirmer');
      return;
    }
    setSaving(true);
    try {
      await axios.delete(`${API}/contract-runs/${transferId}/permanent`);
      toast.success('Transfert supprimé définitivement');
      navigate(-1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
      setShowPermanentDeleteModal(false);
      setPermanentDeleteConfirm('');
    }
  };

  // Fix orphan
  const handleFixOrphan = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/contract-runs/${transferId}/fix-link`);
      toast.success('Transfert réparé');
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatPrice = (amount) => {
    if (!amount) return '0 FCFA';
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  if (!transfer) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Transfert non trouvé</h2>
          <Button onClick={() => navigate(-1)} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>
      </div>
    );
  }

  const departureAddress = transfer.stops?.[0]?.address || 'Non défini';
  const arrivalAddress = transfer.stops?.[transfer.stops?.length - 1]?.address || 'Non défini';
  const departureCity = departureAddress.split(',')[0];
  const arrivalCity = arrivalAddress.split(',')[0];

  // Determine progress stage
  const getProgressStage = () => {
    if (['cancelled'].includes(transfer.status)) return 0;
    if (['planned', 'planifiee'].includes(transfer.status)) return 1;
    if (['en_cours_de_chargement'].includes(transfer.status)) return 2;
    if (['en_transit'].includes(transfer.status)) return 3;
    if (['en_cours_de_dechargement'].includes(transfer.status)) return 4;
    if (['completed'].includes(transfer.status)) return 5;
    return 1;
  };
  
  const progressStage = getProgressStage();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-gray-900">{transfer.runNumber}</h1>
                <StatusBadge status={transfer.status} />
              </div>
              <p className="text-sm text-gray-500">
                {formatDate(transfer.date)} • {transfer.shift === 'day' ? 'Jour' : 'Nuit'}
                {transfer.contractNumber && ` • Contrat ${transfer.contractNumber}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => fetchTransfer()} disabled={saving}>
              <RefreshCw className={cn("w-4 h-4", saving && "animate-spin")} />
            </Button>
            {(canEdit || canAdminEdit) && transfer.status !== 'cancelled' && (
              <Button
                variant="outline"
                size="sm"
                className={transfer.status === 'completed' ? "text-amber-600 border-amber-300 hover:bg-amber-50" : "text-blue-600"}
                onClick={handleEditWithWizard}
                data-testid="edit-transfer-btn"
              >
                <Edit className="w-4 h-4 mr-1" />
                {transfer.status === 'completed' ? 'Corriger (audit)' : 'Modifier'}
              </Button>
            )}
            {!canEdit && !canAdminEdit && transfer.status !== 'cancelled' && transfer.status !== 'completed' && (
              <Button variant="outline" size="sm" disabled className="text-gray-400">
                <Edit className="w-4 h-4 mr-1" />
                Non modifiable
              </Button>
            )}
            <Button variant="outline" size="sm" className="text-blue-600" onClick={handleDuplicate}>
              <Copy className="w-4 h-4 mr-1" />
              Dupliquer
            </Button>
            {/* Admin-only actions for completed transfers */}
            {user?.role === 'admin' && transfer.status === 'completed' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  onClick={() => { setForceCancelReason(''); setShowForceCancelModal(true); }}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Annuler
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => { setPermanentDeleteConfirm(''); setShowPermanentDeleteModal(true); }}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Supprimer
                </Button>
              </>
            )}
            {/* Admin delete button for non-completed, non-cancelled transfers */}
            {user?.role === 'admin' && !['completed', 'cancelled'].includes(transfer.status) && (
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => setShowDeleteModal(true)}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Supprimer
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Progress Stepper */}
      <div className="bg-white border-b px-6 py-3">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          {/* Commercial */}
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium",
              progressStage >= 1 ? "bg-purple-500" : "bg-gray-300"
            )}>
              1
            </div>
            <span className="text-sm font-medium text-gray-700">Commercial</span>
            {progressStage >= 1 && <CheckCircle className="w-4 h-4 text-emerald-500" />}
          </div>
          <div className="flex-1 h-1 bg-gray-200 mx-4">
            <div className={cn("h-full transition-all", progressStage >= 2 ? "bg-blue-500 w-full" : "w-0")} />
          </div>
          {/* Opération */}
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium",
              progressStage >= 2 ? "bg-blue-500" : "bg-gray-300"
            )}>
              2
            </div>
            <span className="text-sm font-medium text-gray-700">Opération</span>
            {progressStage >= 4 && <CheckCircle className="w-4 h-4 text-emerald-500" />}
          </div>
          <div className="flex-1 h-1 bg-gray-200 mx-4">
            <div className={cn("h-full transition-all", progressStage >= 5 ? "bg-emerald-500 w-full" : "w-0")} />
          </div>
          {/* Finance */}
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium",
              progressStage >= 5 ? "bg-emerald-500" : "bg-gray-300"
            )}>
              3
            </div>
            <span className="text-sm font-medium text-gray-700">Finance</span>
            {progressStage >= 5 && <CheckCircle className="w-4 h-4 text-emerald-500" />}
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="p-6">
        <div className="flex gap-6">
          {/* Left Column - 65% */}
          <div className="flex-1 space-y-6" style={{ maxWidth: '65%' }}>
            
            {/* Map + Trajet Section */}
            <Card className="overflow-hidden">
              {/* Map */}
              <div className="h-[280px]">
                <TransportMap
                  departure={departureAddress}
                  arrival={arrivalAddress}
                  stops={transfer.stops?.slice(1, -1) || []}
                  onDistanceCalculated={(distance) => {
                    // Optional: update distance if needed
                  }}
                  showDistanceBadge={!showWizard}
                />
              </div>
              
              {/* Trajet Info Bar */}
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-green-300" />
                    <div>
                      <p className="text-emerald-100 text-xs">Départ</p>
                      <p className="font-semibold">{departureCity}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <Route className="w-5 h-5 mx-auto mb-1 opacity-80" />
                      {transfer.distance && (
                        <p className="text-sm font-medium">{transfer.distance} km</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-emerald-100 text-xs">Arrivée</p>
                      <p className="font-semibold">{arrivalCity}</p>
                    </div>
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                  </div>
                </div>
              </div>
            </Card>

            {/* Trajet enrichi (PM Spec 05/05/2026 — Aller-retour + Multi-stop) */}
            <TripDetailsBlock
              departure={departureAddress}
              arrival={arrivalAddress}
              distanceKm={transfer.distance_km || transfer.distance}
              intermediateStops={(transfer.stops || []).slice(1, -1).map(s => ({
                address: s.address,
                addressComplement: s.addressComplement,
              }))}
              tripType={transfer.tripType}
              returnMerchandiseDescription={transfer.returnMerchandiseDescription}
              returnWeight={transfer.returnWeight}
              departureAddressComplement={(transfer.stops || [])[0]?.addressComplement}
              arrivalAddressComplement={(transfer.stops || [])[(transfer.stops || []).length - 1]?.addressComplement}
            />

            {/* Timeline des étapes — PM Spec 01/06/2026 — Multi-étapes transfert */}
            {(transfer.stops || []).length > 0 && (
              <TransferStopsTimeline
                transfer={transfer}
                canManage={true}
                onStopStatusChange={handleStopStatusChange}
              />
            )}

            {/* Détails de l'Opération */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Détails de l'Opération</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Row 1 */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <Truck className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Véhicule</p>
                      <p className="font-medium text-gray-900">{formatVehicleType(transfer.vehicleType, transfer.vehicleTypeOther) || 'Semi-remorque'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <Weight className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Poids</p>
                      <p className="font-medium text-gray-900">{transfer.weight ? `${transfer.weight}T` : '-'}</p>
                    </div>
                  </div>
                </div>
                {/* Row 2 */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Type d'opération</p>
                      <p className="font-medium text-gray-900">{formatOperationType(transfer.operationType, transfer.operationTypeOther) || 'Transport'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <Package className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Marchandise</p>
                      <p className="font-medium text-gray-900">{formatMerchandiseType(transfer.merchandiseType, transfer.merchandiseTypeOther) || 'Marchandises diverses'}</p>
                    </div>
                  </div>
                </div>
                {/* Highlighted cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Truck className="w-4 h-4 text-blue-600" />
                      <p className="text-xs text-blue-600 font-medium">Quantité de véhicules</p>
                    </div>
                    <p className="text-2xl font-bold text-blue-700">{transfer.numberOfTrucks || 1} camion{(transfer.numberOfTrucks || 1) > 1 ? 's' : ''}</p>
                  </div>
                  <div className="p-4 bg-purple-50 border border-purple-100 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Route className="w-4 h-4 text-purple-600" />
                      <p className="text-xs text-purple-600 font-medium">Classification</p>
                    </div>
                    <p className="text-lg font-bold text-purple-700">
                      {transfer.isInternational ? 'INTERLAND' : 'INTRALAND'}
                    </p>
                    <p className="text-xs text-purple-600">
                      {transfer.isInternational ? 'Transport international (CEDEAO)' : 'Transport national'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Documents & Bons (onglets dédiés) */}
            <Tabs defaultValue="documents" className="w-full">
              <TabsList>
                <TabsTrigger value="documents" data-testid="transfer-tab-documents">Documents</TabsTrigger>
                <TabsTrigger value="bons" data-testid="transfer-tab-bons">Bons &amp; signatures</TabsTrigger>
              </TabsList>
              <TabsContent value="documents" className="mt-4">
                <DocumentsComplianceCenter
                  operationId={transferId}
                  operation={transfer}
                  docData={docData}
                  onAddDoc={openAddDoc}
                  onRequestField={openRequestField}
                  hideSectionD
                  canValidate={hasPermission('operations.validate_documents')}
                />
              </TabsContent>
              <TabsContent value="bons" className="mt-4">
                <div className="space-y-6">
                  {/* PM Spec V3 13/05/2026 — Panneau unifié Bons & signatures */}
                  <LoadingNotesV3Panel
                    operation={transfer}
                    currentUser={user}
                    entityKind="transfer"
                  />
                  {/* OCR Documents (Carte Grise, Permis, PI) */}
                  <OperationOCRDocuments operationId={transferId} />
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Sidebar 35% */}
          <div className="w-[35%] space-y-4">
            {/* Conformité documentaire */}
            <DocumentsComplianceRail
              stats={docData.stats}
              operation={transfer}
              operationId={transferId}
              onShowMissing={() => { window.dispatchEvent(new Event('op-docs-show-missing')); document.getElementById('op-docs-sections')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              onRequestField={() => openRequestField(docData.stats.manquantsList)}
              onImportMultiple={() => openAddDoc('autre')}
              onHistory={() => document.getElementById('op-docs-recent')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            />
            {/* Actions Administratives Card */}
            <Card className="bg-amber-50/50 border-amber-100">
              <CardContent className="p-5 space-y-5">
                {/* Header */}
                <h3 className="text-amber-700 font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Actions Administratives
                </h3>

                {/* Section Responsables */}
                <div className="space-y-4">
                  <p className="text-sm font-medium text-gray-700">Responsables</p>
                  
                  {/* Opérateur */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Opérateur</span>
                      </div>
                      <button className="text-xs text-emerald-600 hover:text-emerald-700 font-medium" onClick={handleOpenOperatorModal} data-testid="assign-operator-btn">
                        {transfer.assignedOperatorName ? 'Changer' : 'Assigner'}
                      </button>
                    </div>
                    {transfer.assignedOperatorName ? (
                      <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-100 rounded-xl cursor-pointer hover:bg-emerald-100 transition-colors" onClick={handleOpenOperatorModal}>
                        <div className="w-9 h-9 bg-emerald-500 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                          {transfer.assignedOperatorName.substring(0, 2).toUpperCase()}
                        </div>
                        <p className="font-medium text-gray-900 text-sm">{transfer.assignedOperatorName}</p>
                      </div>
                    ) : (
                      <div className="p-3 bg-gray-50 border border-gray-100 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors" onClick={handleOpenOperatorModal}>
                        <p className="text-sm text-gray-400 italic">Non assigné</p>
                      </div>
                    )}
                  </div>

                  {/* Field Ops */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Field Ops</span>
                      </div>
                      <button className="text-xs text-emerald-600 hover:text-emerald-700 font-medium" onClick={handleOpenFieldOpsModal} data-testid="assign-fieldops-btn">
                        {transfer.assignedFieldOpsName ? 'Changer' : 'Assigner'}
                      </button>
                    </div>
                    {transfer.assignedFieldOpsName ? (
                      <div className="flex items-center gap-3 p-3 bg-teal-50 border border-teal-100 rounded-xl cursor-pointer hover:bg-teal-100 transition-colors" onClick={handleOpenFieldOpsModal}>
                        <div className="w-9 h-9 bg-teal-500 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                          {transfer.assignedFieldOpsName.substring(0, 2).toUpperCase()}
                        </div>
                        <p className="font-medium text-gray-900 text-sm">{transfer.assignedFieldOpsName}</p>
                      </div>
                    ) : (
                      <div className="p-3 bg-gray-50 border border-gray-100 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors" onClick={handleOpenFieldOpsModal}>
                        <p className="text-sm text-gray-400 italic">Non assigné</p>
                      </div>
                    )}
                  </div>

                  {/* Nombre de voyages (S3) */}
                  <div className="space-y-2">
                    {(() => {
                      const termo = transfer.terminology || 'transfert';
                      const LABELS = { transfert: 'Transfert', operation: 'Opération', course: 'Course', livraison: 'Livraison' };
                      const unitLabel = LABELS[termo] || (termo.charAt(0).toUpperCase() + termo.slice(1));
                      // Cohérence : utiliser le max entre numberOfTrips et le nombre de poids nets saisis
                      const tripsFromWeights = (transfer.tripsNetWeights || []).length;
                      const n = Math.max(transfer.numberOfTrips || 1, tripsFromWeights);
                      return (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Hash className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600">{unitLabel}s (aller-retour)</span>
                              {transfer.status === 'completed' && (
                                <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Correction audit</span>
                              )}
                            </div>
                            {!editingTrips && (
                              <button className="text-xs text-emerald-600 hover:text-emerald-700 font-medium" onClick={() => { setTripsValue(n); setEditingTrips(true); }} data-testid="edit-trips-btn">
                                Modifier
                              </button>
                            )}
                          </div>
                          {editingTrips ? (
                            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                              <button onClick={() => setTripsValue(Math.max(1, tripsValue - 1))} className="w-8 h-8 rounded-lg bg-white border flex items-center justify-center hover:bg-gray-50">
                                <Minus className="w-4 h-4" />
                              </button>
                              <input type="number" min="1" value={tripsValue} onChange={(e) => setTripsValue(Math.max(1, parseInt(e.target.value) || 1))} className="w-16 text-center font-bold text-lg border rounded-lg py-1" data-testid="trips-input" />
                              <button onClick={() => setTripsValue(tripsValue + 1)} className="w-8 h-8 rounded-lg bg-white border flex items-center justify-center hover:bg-gray-50">
                                <Plus className="w-4 h-4" />
                              </button>
                              <Button size="sm" className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={handleSaveTrips} disabled={saving} data-testid="save-trips-btn">
                                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'OK'}
                              </Button>
                              <Button size="sm" variant="ghost" className="text-xs" onClick={() => setEditingTrips(false)}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="p-3 bg-gray-50 border border-gray-100 rounded-xl">
                              <p className="text-sm font-semibold text-gray-900">
                                {n} {unitLabel.toLowerCase()}{n > 1 ? 's' : ''}
                                {n > 1 && <span className="text-xs font-normal text-gray-500 ml-1">(aller-retour)</span>}
                              </p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Poids net par tour */}
                  <div className="space-y-2">
                    {(() => {
                      const existingWeights = transfer.tripsNetWeights || [];
                      // Source de vérité : si des poids existent, leur nombre prime sur numberOfTrips
                      const n = existingWeights.length > 0
                        ? existingWeights.length
                        : (transfer.numberOfTrips || 1);
                      const totalNet = transfer.netWeight != null
                        ? transfer.netWeight
                        : existingWeights.reduce((s, w) => s + (w || 0), 0);
                      const hasNetWeight = totalNet > 0 || existingWeights.some(w => w != null && w > 0);
                      const isPending = transfer.status === 'completed' && !hasNetWeight;

                      return (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Scale className="w-4 h-4 text-emerald-600" />
                              <span className="text-sm text-gray-600 font-medium">Poids net (pesée)</span>
                              {isPending && (
                                <span className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5 animate-pulse">
                                  À saisir
                                </span>
                              )}
                              {hasNetWeight && !editingNetWeight && (
                                <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 font-semibold">
                                  {totalNet.toFixed(1)} T
                                </span>
                              )}
                            </div>
                            {!editingNetWeight && (
                              <button
                                className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                                onClick={() => {
                                  setTripsNetWeights(Array.from({ length: n }, (_, i) => existingWeights[i] ?? ''));
                                  setEditingNetWeight(true);
                                }}
                              >
                                {hasNetWeight ? 'Modifier' : 'Saisir'}
                              </button>
                            )}
                          </div>

                          {/* CTA card quand completed + poids manquant */}
                          {isPending && !editingNetWeight && (
                            <button
                              className="w-full p-3 bg-orange-50 border border-dashed border-orange-300 rounded-xl text-left hover:bg-orange-100 transition-colors"
                              onClick={() => {
                                setTripsNetWeights(Array.from({ length: n }, (_, i) => existingWeights[i] ?? ''));
                                setEditingNetWeight(true);
                              }}
                            >
                              <p className="text-sm font-medium text-orange-700">Saisir le poids net</p>
                              <p className="text-xs text-orange-500 mt-0.5">{n} tour{n > 1 ? 's' : ''} — poids réel au pont bascule</p>
                            </button>
                          )}

                          {/* Résumé quand poids saisi + non en édition */}
                          {hasNetWeight && !editingNetWeight && (
                            <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                              <div className="space-y-1">
                                {existingWeights.map((w, i) => (
                                  <div key={i} className="flex justify-between text-xs">
                                    <span className="text-gray-500">Tour {i + 1}</span>
                                    <span className="font-medium text-emerald-800">{w != null ? `${w} T` : '—'}</span>
                                  </div>
                                ))}
                                <div className="flex justify-between text-sm font-bold pt-1 border-t border-emerald-200 mt-1">
                                  <span className="text-emerald-900">Total net</span>
                                  <span className="text-emerald-700">{totalNet.toFixed(1)} T</span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Grille édition inline */}
                          {editingNetWeight && (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl space-y-3">
                              <p className="text-xs text-blue-700 font-medium">Poids réel pesé par tour (tonnes)</p>
                              <div className="space-y-2">
                                {Array.from({ length: n }, (_, i) => (
                                  <div key={i} className="flex items-center gap-2">
                                    <span className="text-xs text-blue-600 font-medium w-12">Tour {i + 1}</span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.1"
                                      placeholder="0.0"
                                      value={tripsNetWeights[i] ?? ''}
                                      onChange={e => {
                                        const updated = [...tripsNetWeights];
                                        updated[i] = e.target.value === '' ? '' : parseFloat(e.target.value);
                                        setTripsNetWeights(updated);
                                      }}
                                      onKeyDown={e => { if (e.key === 'Tab' && i === n - 1) { e.preventDefault(); handleSaveNetWeights(); }}}
                                      className="flex-1 h-8 text-sm text-center border rounded-lg px-2 focus:border-blue-400 focus:outline-none"
                                      autoFocus={i === 0}
                                    />
                                    <span className="text-xs text-gray-500">T</span>
                                  </div>
                                ))}
                              </div>
                              {tripsNetWeights.some(w => w !== '' && w !== null) && (
                                <div className="flex justify-between text-sm font-bold pt-1 border-t border-blue-200">
                                  <span className="text-blue-900">Total net</span>
                                  <span className="text-blue-700">
                                    {tripsNetWeights.reduce((s, w) => s + (parseFloat(w) || 0), 0).toFixed(1)} T
                                  </span>
                                </div>
                              )}
                              <div className="flex gap-2 justify-end">
                                <Button size="sm" variant="ghost" className="text-xs" onClick={() => setEditingNetWeight(false)}>
                                  <X className="w-3 h-3 mr-1" />Annuler
                                </Button>
                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={handleSaveNetWeights} disabled={saving}>
                                  {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                                  Enregistrer
                                </Button>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Camions */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Camions ({transfer.assignments?.length || (transfer.plannedVehiclePlate ? 1 : 0)}/{transfer.numberOfTrucks || 1})
                        </span>
                      </div>
                      <button 
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                        onClick={handleOpenAssignTruckModal}
                      >
                        + Assigner
                      </button>
                    </div>
                    {(transfer.assignments?.length > 0 || transfer.plannedVehiclePlate) ? (
                      <div 
                        className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-100 rounded-xl cursor-pointer hover:bg-emerald-100 transition-colors"
                        onClick={handleOpenAssignTruckModal}
                      >
                        <div>
                          <p className="font-medium text-gray-900 text-sm">Camion 1</p>
                          <p className="text-xs text-gray-600">
                            {transfer.plannedDriverName || 'Chauffeur'} • {transfer.plannedVehiclePlate}
                          </p>
                        </div>
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      </div>
                    ) : (
                      <div 
                        className="p-3 bg-amber-50 border border-amber-200 rounded-xl cursor-pointer hover:bg-amber-100 transition-colors"
                        onClick={handleOpenAssignTruckModal}
                      >
                        <p className="text-sm text-amber-700 font-medium">Cliquer pour assigner un camion</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Workflow Actions - Simplifié pour piloter l'opération */}
                {!['completed', 'cancelled'].includes(transfer.status) && (
                  <div className="space-y-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Avancement de l'opération</p>
                    
                    {/* Progress Steps */}
                    <div className="flex items-center justify-between px-2">
                      {[
                        { key: 'planned', label: 'Planifié', icon: Calendar },
                        { key: 'chargement', label: 'Chargement', icon: Package },
                        { key: 'transit', label: 'En transit', icon: Truck },
                        { key: 'dechargement', label: 'Déchargement', icon: MapPin },
                        { key: 'completed', label: 'Terminé', icon: CheckCircle }
                      ].map((step, idx) => {
                        const isActive = 
                          (step.key === 'planned' && ['planned', 'planifiee'].includes(transfer.status)) ||
                          (step.key === 'chargement' && transfer.status === 'en_cours_de_chargement') ||
                          (step.key === 'transit' && transfer.status === 'en_transit') ||
                          (step.key === 'dechargement' && transfer.status === 'en_cours_de_dechargement') ||
                          (step.key === 'completed' && transfer.status === 'completed');
                        const isPast = 
                          (step.key === 'planned' && !['planned', 'planifiee'].includes(transfer.status)) ||
                          (step.key === 'chargement' && ['en_transit', 'en_cours_de_dechargement', 'completed'].includes(transfer.status)) ||
                          (step.key === 'transit' && ['en_cours_de_dechargement', 'completed'].includes(transfer.status)) ||
                          (step.key === 'dechargement' && transfer.status === 'completed');
                        const Icon = step.icon;
                        
                        return (
                          <div key={step.key} className="flex flex-col items-center">
                            <div className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center",
                              isActive ? "bg-blue-500 text-white" : isPast ? "bg-emerald-500 text-white" : "bg-gray-200 text-gray-400"
                            )}>
                              {isPast ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                            </div>
                            <span className={cn(
                              "text-[10px] mt-1",
                              isActive ? "text-blue-600 font-medium" : isPast ? "text-emerald-600" : "text-gray-400"
                            )}>{step.label}</span>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Action Button - Shows next step only */}
                    <div className="pt-2">
                      {['planned', 'planifiee'].includes(transfer.status) && (
                        <Button 
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          onClick={handleStartLoading}
                          disabled={saving}
                        >
                          <Package className="w-4 h-4 mr-2" />
                          Démarrer le chargement
                        </Button>
                      )}
                      {transfer.status === 'en_cours_de_chargement' && (
                        <Button 
                          className="w-full bg-amber-600 hover:bg-amber-700"
                          onClick={handleStartTransit}
                          disabled={saving}
                        >
                          <Truck className="w-4 h-4 mr-2" />
                          Départ en transit
                        </Button>
                      )}
                      {transfer.status === 'en_transit' && (
                        <Button 
                          className="w-full bg-purple-600 hover:bg-purple-700"
                          onClick={handleStartUnloading}
                          disabled={saving}
                        >
                          <MapPin className="w-4 h-4 mr-2" />
                          Arrivée - Déchargement
                        </Button>
                      )}
                      {transfer.status === 'en_cours_de_dechargement' && (
                        <Button 
                          className="w-full bg-emerald-600 hover:bg-emerald-700"
                          onClick={handleFinishUnloading}
                          disabled={saving}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Terminer l'opération
                        </Button>
                      )}
                    </div>
                    
                    {/* Cancel button */}
                    <Button 
                      variant="outline"
                      className="w-full text-red-600 border-red-200 hover:bg-red-50"
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Annuler l'opération
                    </Button>

                    {/* History Link */}
                    <button className="w-full flex items-center justify-center gap-2 py-2 text-sm text-gray-500 hover:text-gray-700 border-t border-gray-200">
                      <Clock className="w-4 h-4" />
                      <span>Historique complet</span>
                    </button>
                  </div>
                )}

                {/* Completed/Cancelled state */}
                {transfer.status === 'completed' && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
                    <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                    <p className="font-medium text-emerald-800">Opération terminée</p>
                  </div>
                )}
                {transfer.status === 'cancelled' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-center">
                    <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <p className="font-medium text-red-800">Opération annulée</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes Card */}
            {transfer.notes && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{transfer.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier le transfert</DialogTitle>
            <DialogDescription>
              Modifiez les informations du transfert
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Départ</Label>
                <Input
                  value={editForm.departure_address}
                  onChange={(e) => setEditForm({ ...editForm, departure_address: e.target.value })}
                  placeholder="Adresse de départ"
                />
              </div>
              <div>
                <Label>Arrivée</Label>
                <Input
                  value={editForm.arrival_address}
                  onChange={(e) => setEditForm({ ...editForm, arrival_address: e.target.value })}
                  placeholder="Adresse d'arrivée"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Véhicule (plaque)</Label>
                <Input
                  value={editForm.vehicle_plate}
                  onChange={(e) => setEditForm({ ...editForm, vehicle_plate: e.target.value })}
                  placeholder="Ex: CI 1234 AB"
                />
              </div>
              <div>
                <Label>Chauffeur</Label>
                <Input
                  value={editForm.driver_name}
                  onChange={(e) => setEditForm({ ...editForm, driver_name: e.target.value })}
                  placeholder="Nom du chauffeur"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Poids (tonnes)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editForm.weight}
                  onChange={(e) => setEditForm({ ...editForm, weight: e.target.value })}
                  placeholder="Ex: 12.5"
                />
              </div>
              <div>
                <Label>Type de marchandise</Label>
                <Input
                  value={editForm.merchandiseType}
                  onChange={(e) => setEditForm({ ...editForm, merchandiseType: e.target.value })}
                  placeholder="Ex: Ciment, Riz..."
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Notes additionnelles..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal (non-completed transfers) */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer le transfert</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer ce transfert ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Cancel Modal (completed transfers — admin only) */}
      <Dialog open={showForceCancelModal} onOpenChange={(open) => { setShowForceCancelModal(open); if (!open) setForceCancelReason(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <XCircle className="w-5 h-5" />
              Annuler ce transfert terminé
            </DialogTitle>
            <DialogDescription>
              Ce transfert est marqué comme <strong>Terminé</strong>. Cette annulation forcée est tracée dans l'audit.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <Label htmlFor="cancel-reason" className="text-sm font-medium">
              Motif d'annulation <span className="text-red-500">*</span>
            </Label>
            <div className="flex flex-col gap-2">
              {['Erreur de saisie', 'Doublon', 'Annulation client', 'Opération test'].map(preset => (
                <Button
                  key={preset}
                  variant={forceCancelReason === preset ? 'default' : 'outline'}
                  size="sm"
                  className="justify-start"
                  onClick={() => setForceCancelReason(preset)}
                >
                  {preset}
                </Button>
              ))}
            </div>
            <Textarea
              id="cancel-reason"
              placeholder="Ou saisissez un motif personnalisé..."
              value={forceCancelReason}
              onChange={e => setForceCancelReason(e.target.value)}
              rows={2}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForceCancelModal(false)}>
              Retour
            </Button>
            <Button
              variant="default"
              className="bg-orange-600 hover:bg-orange-700"
              onClick={handleForceCancel}
              disabled={saving || !forceCancelReason.trim()}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmer l'annulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Modal (admin only) */}
      <Dialog open={showPermanentDeleteModal} onOpenChange={(open) => { setShowPermanentDeleteModal(open); if (!open) setPermanentDeleteConfirm(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Suppression définitive
            </DialogTitle>
            <DialogDescription>
              Cette action est <strong>irréversible</strong>. Le transfert sera effacé définitivement de la base de données.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
              Pour confirmer, saisissez le numéro du transfert : <strong>{transfer?.runNumber}</strong>
            </div>
            <Input
              placeholder={transfer?.runNumber}
              value={permanentDeleteConfirm}
              onChange={e => setPermanentDeleteConfirm(e.target.value)}
              className="border-red-300 focus:border-red-500"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPermanentDeleteModal(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handlePermanentDelete}
              disabled={saving || permanentDeleteConfirm !== transfer?.runNumber}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit/Duplicate Wizard */}
      {transfer && (
        <CreateTransferWizard
          isOpen={showWizard}
          onClose={() => setShowWizard(false)}
          onSuccess={handleWizardSuccess}
          contractId={transfer.contractId}
          contractNumber={transfer.contractNumber || 'N/A'}
          clientName={transfer.clientName || 'N/A'}
          terminology="Transfert"
          mode={wizardMode}
          existingTransfer={transfer}
        />
      )}

      {/* Assign Truck Modal */}
      <Dialog open={showAssignTruckModal} onOpenChange={(open) => { setShowAssignTruckModal(open); if (!open) { setVehicleSearchTerm(''); setDriverSearchTerm(''); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Assigner un camion
            </DialogTitle>
            <DialogDescription>
              Sélectionnez un véhicule et un chauffeur pour ce transfert
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Vehicle Selection with Search */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Véhicule</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par plaque ou modèle..."
                  value={vehicleSearchTerm}
                  onChange={(e) => setVehicleSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="truck-vehicle-search"
                />
                {vehicleSearchTerm && (
                  <button onClick={() => setVehicleSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="max-h-[150px] overflow-y-auto space-y-1 border rounded-lg p-1">
                {(() => {
                  const q = vehicleSearchTerm.toLowerCase().trim();
                  const filtered = availableVehicles.filter(v => {
                    const plate = (v.license_plate || v.licensePlate || v.registration || v.plateNumber || '').toLowerCase();
                    const model = (v.model || v.vehicleType || v.type || '').toLowerCase();
                    return !q || plate.includes(q) || model.includes(q);
                  });
                  if (filtered.length === 0) return <p className="text-center text-gray-400 text-xs py-3">{q ? `Aucun véhicule pour "${vehicleSearchTerm}"` : 'Aucun véhicule'}</p>;
                  return filtered.map(v => {
                    const plate = v.license_plate || v.licensePlate || v.registration || v.plateNumber || '';
                    const label = v.model || v.vehicleType || v.type || '';
                    const isSelected = assignmentForm.vehicleId === v.id;
                    return (
                      <div key={v.id} data-testid={`truck-vehicle-item-${v.id}`}
                        className={cn("flex items-center justify-between px-3 py-2 rounded-md cursor-pointer text-sm transition-colors", isSelected ? "bg-emerald-50 border border-emerald-200" : "hover:bg-gray-50")}
                        onClick={() => { setAssignmentForm({ ...assignmentForm, vehicleId: v.id, vehiclePlate: plate }); setVehicleSearchTerm(''); }}
                      >
                        <div className="flex items-center gap-2">
                          <Truck className="w-3 h-3 text-gray-400" />
                          <span className="font-medium">{plate}</span>
                          <span className="text-gray-400">-</span>
                          <span className="text-gray-500">{label}</span>
                        </div>
                        {isSelected && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                      </div>
                    );
                  });
                })()}
              </div>
              <p className="text-xs text-gray-500">Ou saisissez manuellement :</p>
              <Input
                placeholder="Immatriculation (ex: CI 1234 AB)"
                value={assignmentForm.vehiclePlate}
                onChange={(e) => setAssignmentForm({ ...assignmentForm, vehiclePlate: e.target.value, vehicleId: '' })}
                data-testid="truck-vehicle-manual"
              />
            </div>

            {/* Driver Selection with Search */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Chauffeur</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par nom ou téléphone..."
                  value={driverSearchTerm}
                  onChange={(e) => setDriverSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="truck-driver-search"
                />
                {driverSearchTerm && (
                  <button onClick={() => setDriverSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="max-h-[150px] overflow-y-auto space-y-1 border rounded-lg p-1">
                {(() => {
                  const q = driverSearchTerm.toLowerCase().trim();
                  const filtered = availableDrivers.filter(d => {
                    const name = `${d.first_name || d.firstName || ''} ${d.last_name || d.lastName || ''}`.toLowerCase();
                    const phone = (d.phone || d.telephone || '').toLowerCase();
                    return !q || name.includes(q) || phone.includes(q);
                  });
                  if (filtered.length === 0) return <p className="text-center text-gray-400 text-xs py-3">{q ? `Aucun chauffeur pour "${driverSearchTerm}"` : 'Aucun chauffeur'}</p>;
                  return filtered.map(d => {
                    const name = `${d.first_name || d.firstName || ''} ${d.last_name || d.lastName || ''}`.trim();
                    const phone = d.phone || d.telephone || '';
                    const isSelected = assignmentForm.driverId === d.id;
                    return (
                      <div key={d.id} data-testid={`truck-driver-item-${d.id}`}
                        className={cn("flex items-center justify-between px-3 py-2 rounded-md cursor-pointer text-sm transition-colors", isSelected ? "bg-emerald-50 border border-emerald-200" : "hover:bg-gray-50")}
                        onClick={() => { setAssignmentForm({ ...assignmentForm, driverId: d.id, driverName: name }); setDriverSearchTerm(''); }}
                      >
                        <div className="flex items-center gap-2">
                          <User className="w-3 h-3 text-gray-400" />
                          <span className="font-medium">{name}</span>
                          {phone && <span className="text-xs text-gray-400">({phone})</span>}
                        </div>
                        {isSelected && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                      </div>
                    );
                  });
                })()}
              </div>
              <p className="text-xs text-gray-500">Ou saisissez manuellement :</p>
              <Input
                placeholder="Nom du chauffeur"
                value={assignmentForm.driverName}
                onChange={(e) => setAssignmentForm({ ...assignmentForm, driverName: e.target.value, driverId: '' })}
                data-testid="truck-driver-manual"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignTruckModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleAssignTruck} 
              disabled={saving || (!assignmentForm.vehiclePlate && !assignmentForm.driverName)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="assign-truck-submit"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Assigner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* S2: Operator Assignment Modal */}
      <Dialog open={showOperatorModal} onOpenChange={setShowOperatorModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-600" />
              Assigner un opérateur
            </DialogTitle>
            <DialogDescription>Sélectionnez l'opérateur responsable de ce transfert</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {operators.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Aucun opérateur disponible</p>
            ) : (
              <div className="max-h-[250px] overflow-y-auto space-y-1">
                {operators.map(op => {
                  const name = `${op.firstName || ''} ${op.lastName || ''}`.trim();
                  const isSelected = selectedOperatorId === op.id;
                  return (
                    <div key={op.id} data-testid={`operator-item-${op.id}`}
                      className={cn("flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-colors", isSelected ? "bg-emerald-50 border border-emerald-200" : "hover:bg-gray-50 border border-transparent")}
                      onClick={() => setSelectedOperatorId(op.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold", isSelected ? "bg-emerald-500 text-white" : "bg-gray-200 text-gray-600")}>
                          {name.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{name}</p>
                          {op.email && <p className="text-xs text-gray-400">{op.email}</p>}
                        </div>
                      </div>
                      {isSelected && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOperatorModal(false)}>Annuler</Button>
            <Button onClick={handleAssignOperator} disabled={savingAssignment || !selectedOperatorId} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-operator-btn">
              {savingAssignment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Assigner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* S2: Field Ops Assignment Modal */}
      <Dialog open={showFieldOpsModal} onOpenChange={setShowFieldOpsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-teal-600" />
              Assigner un Field Ops
            </DialogTitle>
            <DialogDescription>Sélectionnez le Field Ops responsable sur le terrain</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {fieldOpsUsers.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Aucun utilisateur disponible</p>
            ) : (
              <div className="max-h-[250px] overflow-y-auto space-y-1">
                {fieldOpsUsers.map(fo => {
                  const name = `${fo.firstName || ''} ${fo.lastName || ''}`.trim();
                  const isSelected = selectedFieldOpsId === fo.id;
                  return (
                    <div key={fo.id} data-testid={`fieldops-item-${fo.id}`}
                      className={cn("flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-colors", isSelected ? "bg-teal-50 border border-teal-200" : "hover:bg-gray-50 border border-transparent")}
                      onClick={() => setSelectedFieldOpsId(fo.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold", isSelected ? "bg-teal-500 text-white" : "bg-gray-200 text-gray-600")}>
                          {name.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{name}</p>
                          {fo.email && <p className="text-xs text-gray-400">{fo.email}</p>}
                        </div>
                      </div>
                      {isSelected && <CheckCircle className="w-4 h-4 text-teal-500" />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFieldOpsModal(false)}>Annuler</Button>
            <Button onClick={handleAssignFieldOps} disabled={savingAssignment || !selectedFieldOpsId} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-fieldops-btn">
              {savingAssignment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Assigner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loading Note Modal (PM Spec LOT 2 — 05/05/2026) */}
      <LoadingNoteWizardModal
        open={loadingNoteModal.open}
        onOpenChange={(open) => setLoadingNoteModal({ open, type: loadingNoteModal.type })}
        operation={transfer}
        type={loadingNoteModal.type}
        entityKind="transfer"
        onSuccess={() => fetchTransfer()}
      />

      {/* Modales documents (centre de conformité) */}
      {transfer && (
        <>
          <AddDocumentModal
            open={addDocState.open}
            onOpenChange={(v) => setAddDocState((s) => ({ ...s, open: v }))}
            operationId={transferId}
            defaultType={addDocState.type}
            initialFiles={addDocState.files}
            onUploaded={docData.refetch}
          />
          <RequestFieldModal
            open={reqFieldState.open}
            onOpenChange={(v) => setReqFieldState((s) => ({ ...s, open: v }))}
            operationId={transferId}
            missingLabels={reqFieldState.labels}
          />
        </>
      )}
    </div>
  );
};

export default TransferDetail;
